import { Alert, Avatar, Colors, FlexBox, FormError, Spinner } from '@helloself/hello-self-ui';
import React, { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';
import AvatarEdit from 'react-avatar-edit';

import { useAppDispatch } from '../../../../../app/hooks';
import { Button, Heading, IconInfo } from '../../../../../ui';
import { ProfilePhoto, PutSuccessResponse } from '../../../model/types';
import { setProfilePhoto } from '../../../profileSlice';
import useProfileRecordEvent from '../../hooks/useProfileRecordEvent';
import styles from './ProfilePictureField.module.scss';

const MAX_PHOTO_SIZE = 5;

type ProfilePictureFieldProps = {
    firstName: string;
    lastName: string;
    imageUrl: string;
    handlePhotoSubmit: (values: ProfilePhoto) => Promise<PutSuccessResponse>;
    setProfilePicturePreview?: Dispatch<SetStateAction<string>>;
};

export function ProfilePictureField({
    imageUrl,
    firstName,
    lastName,
    handlePhotoSubmit,
    setProfilePicturePreview = () => {
        return;
    },
}: ProfilePictureFieldProps) {
    const [isEdit, setIsEdit] = useState(false);
    const [showError, setShowError] = useState(false);
    const [profilePicture, setProfilePicture] = useState(imageUrl);
    const [onSaveStatus, setOnSaveStatus] = useState<'success' | 'error' | ''>('');
    const [onSaveMessage, setOnSaveMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const dispatch = useAppDispatch();
    const { recordProfileEvent } = useProfileRecordEvent();
    let preview = '';

    const onBeforeFileLoad = ({ target }: ChangeEvent<HTMLInputElement>) => {
        if (target.files !== null && target.files[0].size > MAX_PHOTO_SIZE * 1048576) {
            setShowError(true);
            target.value = '';
        } else {
            setShowError(false);
        }
    };

    const onCrop = (crop: string) => {
        preview = crop;
    };

    const onSave = async (preview: string) => {
        setLoading(true);
        const type = (preview.match(/^data:([^;]+);/) || '')[1];
        const base64 = await fetch(preview);
        const blob = await base64.blob();

        if (blob) {
            const response = await handlePhotoSubmit({
                blob,
                type,
            });

            if (response.ok) {
                setOnSaveStatus('success');
                setOnSaveMessage('Successfully uploaded new picture');
                setProfilePicture(preview);
                setProfilePicturePreview(preview);
                dispatch(setProfilePhoto(preview));
            } else {
                setProfilePicturePreview('');
                setOnSaveStatus('error');
                setOnSaveMessage(response.errorMessage || 'Failed to upload new picture');
            }
        }
        setLoading(false);
    };

    const handleUploadClick = () => {
        setIsEdit(true);
        recordProfileEvent('Clicked Upload Photo', {
            category: 'Profile-Setup',
            'Clicked from Location': 'Profile Editor',
        });
    };

    return (
        <div className="flex flex-col gap-3">
            <Heading level={4}>Profile picture</Heading>
            <FlexBox flexDirection="column">
                <FlexBox gap="8px">
                    {loading ? (
                        <Spinner color={Colors.hsBlue} height="64px" width="64px" />
                    ) : (
                        <Avatar
                            size="64px"
                            profile={{
                                firstName,
                                lastName,
                                profilePicture,
                            }}
                            backgroundColor={Colors.ExpertBlue300}
                            textColor={Colors.Neutral500}
                            fontSize="16px"
                        />
                    )}
                    <FlexBox justifyContent="center" flexDirection="column" gap="8px">
                        <div className="flex text-sm gap-1 items-center">
                            <span>Make sure the file is below {MAX_PHOTO_SIZE}MB</span>
                            <IconInfo />
                        </div>
                        <FlexBox>
                            <Button
                                size="small"
                                onClick={() => {
                                    setIsEdit(true);
                                    handleUploadClick();
                                }}
                            >
                                Upload a photo
                            </Button>
                        </FlexBox>
                        {onSaveStatus !== '' && (
                            <Alert fontSize="14px" type={onSaveStatus}>
                                {onSaveMessage}
                            </Alert>
                        )}
                    </FlexBox>
                </FlexBox>

                <FlexBox
                    className={isEdit ? styles.fadeIn : styles.fadeOut}
                    overflow={isEdit ? 'auto' : 'hidden'}
                    flexDirection="column"
                    gap="8px"
                    aria-label={`upload photo input is ${isEdit ? 'shown' : 'hidden'}`}
                >
                    <AvatarEdit
                        imageHeight={535}
                        imageWidth={535}
                        width={535}
                        height={200}
                        onCrop={onCrop}
                        onBeforeFileLoad={onBeforeFileLoad}
                    />
                    {showError && (
                        <FormError textSize="xsmall" margin="0">
                            File too large, must be less than {MAX_PHOTO_SIZE}MB
                        </FormError>
                    )}
                    <FlexBox gap="8px">
                        <FlexBox flex="1">
                            <Button
                                onClick={() => {
                                    setIsEdit(false);
                                }}
                            >
                                Exit
                            </Button>
                        </FlexBox>
                        <FlexBox flex="1" justifyContent="flex-end">
                            <Button
                                disabled={showError}
                                onClick={() => {
                                    if (preview !== '') {
                                        onSave(preview);
                                    }
                                    setIsEdit(false);
                                }}
                            >
                                Save
                            </Button>
                        </FlexBox>
                    </FlexBox>
                </FlexBox>
            </FlexBox>
        </div>
    );
}
